/**
 * Custom Animation Hooks
 * Reusable hooks for common animation patterns
 */

import { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { useAnimation as useFramerAnimation } from 'framer-motion';
import { useSpring, useSpringValue } from '@react-spring/web';
import { useAnimation, useAnimationConfig, useReducedMotion } from './context';
import { throttle, debounce, isInViewport, getOptimalDuration } from './utils';
import type { AnimationControls } from 'framer-motion';
import type { SpringValue } from '@react-spring/web';

/**
 * Hook for scroll-triggered animations
 */
export const useScrollAnimation = (
  threshold: number = 0.1,
  triggerOnce: boolean = true
) => {
  const [isVisible, setIsVisible] = useState(false);
  const [scrollY, setScrollY] = useState(0);
  const [scrollDirection, setScrollDirection] = useState<'up' | 'down'>('down');
  const elementRef = useRef<HTMLElement>(null);
  const config = useAnimationConfig();
  const isReducedMotion = useReducedMotion();
  const controls = useFramerAnimation();
  const lastScrollY = useRef(0);

  // Throttled scroll handler for performance
  const handleScroll = useMemo(
    () => throttle(() => {
      const currentScrollY = window.scrollY;
      
      // Update scroll direction
      setScrollDirection(currentScrollY > lastScrollY.current ? 'down' : 'up');
      lastScrollY.current = currentScrollY;
      setScrollY(currentScrollY);

      // Check element visibility
      if (elementRef.current && !isVisible) {
        const rect = elementRef.current.getBoundingClientRect();
        const windowHeight = window.innerHeight;
        const elementTop = rect.top;
        const elementHeight = rect.height;
        
        // Calculate if element is in viewport based on threshold
        const triggerPoint = windowHeight * (1 - threshold);
        const isInView = elementTop < triggerPoint && elementTop + elementHeight > 0;
        
        if (isInView) {
          setIsVisible(true);
          if (!isReducedMotion) {
            controls.start('visible');
          }
        }
      }
    }, 16), // ~60fps
    [threshold, isVisible, isReducedMotion, controls]
  );

  useEffect(() => {
    if (isReducedMotion) {
      setIsVisible(true);
      return;
    }

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll(); // Check initial state
    
    return () => window.removeEventListener('scroll', handleScroll);
  }, [handleScroll, isReducedMotion]);

  // Reset visibility if triggerOnce is false
  useEffect(() => {
    if (!triggerOnce && elementRef.current) {
      const observer = new IntersectionObserver(
        ([entry]) => {
          if (!entry.isIntersecting && isVisible) {
            setIsVisible(false);
            controls.start('hidden');
          }
        },
        { threshold: 0 }
      );
      
      observer.observe(elementRef.current);
      return () => observer.disconnect();
    }
  }, [triggerOnce, isVisible, controls]);

  return {
    ref: elementRef,
    isVisible,
    scrollY,
    scrollDirection,
    controls,
    // Animation variants optimized for scroll
    variants: {
      hidden: { 
        opacity: 0, 
        y: isReducedMotion ? 0 : 50,
        transition: { duration: config.customTimings.fast }
      },
      visible: { 
        opacity: 1, 
        y: 0,
        transition: { 
          duration: config.customTimings.normal,
          ease: config.customTimings.defaultEasing
        }
      }
    }
  };
};

/**
 * Hook for intersection observer-based animations
 */
export const useIntersectionAnimation = (
  options: IntersectionObserverInit = {}
) => {
  const [isIntersecting, setIsIntersecting] = useState(false);
  const [intersectionRatio, setIntersectionRatio] = useState(0);
  const elementRef = useRef<HTMLElement>(null);
  const config = useAnimationConfig();
  const isReducedMotion = useReducedMotion();
  const controls = useFramerAnimation();

  const defaultOptions: IntersectionObserverInit = {
    threshold: [0, 0.25, 0.5, 0.75, 1],
    rootMargin: '0px 0px -50px 0px',
    ...options
  };

  useEffect(() => {
    if (!elementRef.current || isReducedMotion) {
      if (isReducedMotion) {
        setIsIntersecting(true);
        setIntersectionRatio(1);
      }
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsIntersecting(entry.isIntersecting);
        setIntersectionRatio(entry.intersectionRatio);
        
        if (entry.isIntersecting) {
          controls.start('visible');
        } else {
          controls.start('hidden');
        }
      },
      defaultOptions
    );

    observer.observe(elementRef.current);
    
    return () => observer.disconnect();
  }, [controls, isReducedMotion, defaultOptions]);

  return {
    ref: elementRef,
    isIntersecting,
    intersectionRatio,
    controls,
    // Progressive animation based on intersection ratio
    variants: {
      hidden: { 
        opacity: 0,
        scale: isReducedMotion ? 1 : 0.95,
        y: isReducedMotion ? 0 : 20
      },
      visible: { 
        opacity: intersectionRatio,
        scale: 1,
        y: 0,
        transition: {
          duration: config.customTimings.normal * intersectionRatio,
          ease: config.customTimings.defaultEasing
        }
      }
    }
  };
};

/**
 * Hook for gesture-based animations (touch and mouse)
 */
export const useGestureAnimation = () => {
  const [isPressed, setIsPressed] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [touchStart, setTouchStart] = useState({ x: 0, y: 0 });
  const elementRef = useRef<HTMLElement>(null);
  const config = useAnimationConfig();
  const isReducedMotion = useReducedMotion();
  
  // Spring values for smooth gesture animations
  const springProps = useSpring({
    scale: isPressed ? 0.95 : isHovered ? 1.05 : 1,
    x: dragOffset.x,
    y: dragOffset.y,
    config: {
      tension: config.customTimings.springConfig.tension,
      friction: config.customTimings.springConfig.friction
    },
    immediate: isReducedMotion
  });

  // Mouse event handlers
  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    if (isReducedMotion) return;
    setIsPressed(true);
    setTouchStart({ x: e.clientX, y: e.clientY });
  }, [isReducedMotion]);

  const handleMouseUp = useCallback(() => {
    setIsPressed(false);
    setDragOffset({ x: 0, y: 0 });
  }, []);

  const handleMouseMove = useCallback((e: React.MouseEvent) => {
    if (!isPressed || isReducedMotion) return;
    
    const deltaX = e.clientX - touchStart.x;
    const deltaY = e.clientY - touchStart.y;
    
    // Limit drag distance for better UX
    const maxDrag = 20;
    setDragOffset({
      x: Math.max(-maxDrag, Math.min(maxDrag, deltaX * 0.5)),
      y: Math.max(-maxDrag, Math.min(maxDrag, deltaY * 0.5))
    });
  }, [isPressed, touchStart, isReducedMotion]);

  const handleMouseEnter = useCallback(() => {
    if (!isReducedMotion) setIsHovered(true);
  }, [isReducedMotion]);

  const handleMouseLeave = useCallback(() => {
    setIsHovered(false);
    setIsPressed(false);
    setDragOffset({ x: 0, y: 0 });
  }, []);

  // Touch event handlers
  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    if (isReducedMotion) return;
    const touch = e.touches[0];
    setIsPressed(true);
    setTouchStart({ x: touch.clientX, y: touch.clientY });
  }, [isReducedMotion]);

  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    if (!isPressed || isReducedMotion) return;
    
    const touch = e.touches[0];
    const deltaX = touch.clientX - touchStart.x;
    const deltaY = touch.clientY - touchStart.y;
    
    const maxDrag = 15; // Slightly less for touch
    setDragOffset({
      x: Math.max(-maxDrag, Math.min(maxDrag, deltaX * 0.3)),
      y: Math.max(-maxDrag, Math.min(maxDrag, deltaY * 0.3))
    });
  }, [isPressed, touchStart, isReducedMotion]);

  const handleTouchEnd = useCallback(() => {
    setIsPressed(false);
    setDragOffset({ x: 0, y: 0 });
  }, []);

  return {
    ref: elementRef,
    springProps,
    isPressed,
    isHovered,
    dragOffset,
    // Event handlers
    gestureHandlers: {
      onMouseDown: handleMouseDown,
      onMouseUp: handleMouseUp,
      onMouseMove: handleMouseMove,
      onMouseEnter: handleMouseEnter,
      onMouseLeave: handleMouseLeave,
      onTouchStart: handleTouchStart,
      onTouchMove: handleTouchMove,
      onTouchEnd: handleTouchEnd,
    },
    // Framer Motion variants for gesture states
    variants: {
      idle: { 
        scale: 1,
        x: 0,
        y: 0
      },
      hover: { 
        scale: isReducedMotion ? 1 : 1.02,
        transition: { duration: config.customTimings.fast }
      },
      pressed: { 
        scale: isReducedMotion ? 1 : 0.98,
        transition: { duration: config.customTimings.fast }
      }
    }
  };
};

/**
 * Hook for FPS and performance monitoring
 */
export const usePerformanceMonitor = () => {
  const [fps, setFps] = useState(60);
  const [frameTime, setFrameTime] = useState(0);
  const [isLowPerformance, setIsLowPerformance] = useState(false);
  const { performanceMonitor, performanceMetrics } = useAnimation();
  const frameCount = useRef(0);
  const lastTime = useRef(performance.now());
  const rafId = useRef<number>();

  // Custom FPS measurement for more granular control
  const measureFPS = useCallback(() => {
    const currentTime = performance.now();
    const deltaTime = currentTime - lastTime.current;
    
    frameCount.current++;
    
    if (deltaTime >= 1000) {
      const currentFPS = Math.round((frameCount.current * 1000) / deltaTime);
      setFps(currentFPS);
      setFrameTime(deltaTime / frameCount.current);
      setIsLowPerformance(currentFPS < 50);
      
      frameCount.current = 0;
      lastTime.current = currentTime;
    }
    
    rafId.current = requestAnimationFrame(measureFPS);
  }, []);

  useEffect(() => {
    rafId.current = requestAnimationFrame(measureFPS);
    
    return () => {
      if (rafId.current) {
        cancelAnimationFrame(rafId.current);
      }
    };
  }, [measureFPS]);

  // Performance recommendations based on current metrics
  const getPerformanceRecommendations = useCallback(() => {
    const recommendations: string[] = [];
    
    if (fps < 50) {
      recommendations.push('Consider reducing animation complexity');
      recommendations.push('Enable GPU acceleration for transforms');
    }
    
    if (performanceMetrics.activeAnimations > 10) {
      recommendations.push('Too many concurrent animations');
    }
    
    if (frameTime > 20) {
      recommendations.push('Frame time is high - optimize render cycles');
    }
    
    return recommendations;
  }, [fps, frameTime, performanceMetrics.activeAnimations]);

  // Auto-optimization suggestions
  const shouldReduceAnimations = fps < 45;
  const shouldDisableComplexEffects = fps < 35;
  
  return {
    fps,
    frameTime,
    isLowPerformance,
    performanceMetrics,
    shouldReduceAnimations,
    shouldDisableComplexEffects,
    recommendations: getPerformanceRecommendations(),
    // Performance monitoring controls
    startMonitoring: () => performanceMonitor.start(),
    stopMonitoring: () => performanceMonitor.stop(),
    // Animation lifecycle tracking
    trackAnimation: {
      start: () => performanceMonitor.registerAnimation(),
      end: () => performanceMonitor.unregisterAnimation()
    }
  };
};

/**
 * Hook for coordinated animation sequences
 */
export const useAnimationSequence = (
  animations: Array<{
    delay: number;
    duration: number;
    animation: () => Promise<void> | void;
  }>
) => {
  const [currentStep, setCurrentStep] = useState(-1);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isComplete, setIsComplete] = useState(false);
  const config = useAnimationConfig();
  const isReducedMotion = useReducedMotion();

  const playSequence = useCallback(async () => {
    if (isPlaying || isComplete) return;
    
    setIsPlaying(true);
    setCurrentStep(0);
    
    for (let i = 0; i < animations.length; i++) {
      const { delay, duration, animation } = animations[i];
      
      // Wait for delay
      if (delay > 0 && !isReducedMotion) {
        await new Promise(resolve => setTimeout(resolve, delay * 1000));
      }
      
      setCurrentStep(i);
      
      // Execute animation
      await Promise.resolve(animation());
      
      // Wait for duration if not reduced motion
      if (!isReducedMotion) {
        await new Promise(resolve => setTimeout(resolve, duration * 1000));
      }
    }
    
    setIsPlaying(false);
    setIsComplete(true);
    setCurrentStep(-1);
  }, [animations, isPlaying, isComplete, isReducedMotion]);

  const resetSequence = useCallback(() => {
    setCurrentStep(-1);
    setIsPlaying(false);
    setIsComplete(false);
  }, []);

  return {
    currentStep,
    isPlaying,
    isComplete,
    playSequence,
    resetSequence
  };
};